import { useLayoutEffect, useReducer, useRef, useState } from 'react';
import type { LiniyaNabora, WorkplaceLineBrief } from './workplaceApi';
import { razobratNomer, SLOVA_OTKAZA, type Nomer } from './nomerNabora';

/**
 * Ручной набор (ТЗ §2.19, карта 2.7).
 *
 * Линия у рабочего места одна, поэтому её чип подсвечен и выбирать не из
 * чего — так и показано. Как только линий станет несколько, здесь появится
 * определение по коду номера и ручной выбор; ставить выбор из одного варианта
 * сейчас значит спрашивать человека о том, на что нет ответа.
 *
 * Ручной звонок — такой же звонок: он попадает в журнал, в личную статистику
 * и (когда появятся обзвоны) учитывается дайлерами, чтобы робот не позвонил
 * этому клиенту вторым заходом.
 *
 * ⛔ `s713` Д-13: поле не «любая строка, из которой потом вынут цифры», а номер. Набор,
 * клавиши панели и вставка идут через одно правило (`nomerNabora.ts`): российский номер
 * встаёт в маску +7, международный остаётся международным, а буквы, второй номер и лишние
 * цифры не принимаются — поле остаётся прежним и словами говорит почему. Курсор и
 * правка в середине номера работают как в обычном поле.
 */
interface Props {
  initialNumber?: string;
  draft?: Nomer;
  onDraft?: (nomer: Nomer) => void;
  editingDisabled?: boolean;
  line: WorkplaceLineBrief | null;
  busy: boolean;
  canDial: boolean;
  onCall: (number: string) => void;
  /** `s726`: линии на выбор; одна или ни одной — выбора нет, звонит линия рабочего места. */
  linii?: LiniyaNabora[];
  vybrana?: number | null;
  onVybor?: (id: number) => void;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

/** Значимые знаки номера — без скобок, пробелов и дефисов маски. */
const znachimyh = (s: string) => s.replace(/[^\d*#+]/g, '').length;

/** Место в строке сразу за N-м значимым знаком — туда встаёт курсор после маски. */
function mestoPosle(s: string, n: number): number {
  if (n <= 0) return 0;
  let vidno = 0;
  for (let i = 0; i < s.length; i++) {
    if (/[\d*#+]/.test(s[i]) && ++vidno === n) return i + 1;
  }

  return s.length;
}

export function ManualDial({ line, busy, canDial, onCall, linii = [], vybrana = null, onVybor, initialNumber, draft, onDraft, editingDisabled = false }: Props) {
  const [localNomer, setLocalNomer] = useState<Nomer>(() => {
    const parsed = initialNumber ? razobratNomer(initialNumber, true) : null;

    return parsed?.ok ? parsed.nomer : { pole: '+7', vid: 'nepolnyy', nabor: null, zametka: null };
  });
  const nomer = draft ?? localNomer;
  const setNomer = (value: Nomer) => { setLocalNomer(value); onDraft?.(value); };
  const [otkaz, setOtkaz] = useState<string | null>(null);
  const pole = useRef<HTMLInputElement | null>(null);
  /*
   * ⛔ КУРСОР ОСТАЁТСЯ НА МЕСТЕ ПРАВКИ. Маска переписывает строку, и браузер после этого
   * ставит курсор в конец: поправил цифру в середине — следующая ушла бы в хвост номера.
   * Запоминаем, за каким по счёту значимым знаком стоял курсор, и возвращаем его туда же.
   */
  const kursor = useRef<number | null>(null);
  const prezhniyKursor = useRef(0);
  const [, pererisovat] = useReducer((n: number) => n + 1, 0);
  useLayoutEffect(() => {
    const el = pole.current;
    if (kursor.current !== null && el !== null && document.activeElement === el) {
      const mesto = mestoPosle(el.value, kursor.current);
      el.setSelectionRange(mesto, mesto);
    }
    kursor.current = null;
  });
  const zapomnitKursor = (el: HTMLInputElement) => {
    prezhniyKursor.current = znachimyh(el.value.slice(0, el.selectionStart ?? el.value.length));
  };

  /*
   * `s724`: в пустом поле стоит заготовка «+7», а текст в нём по центру — клик мышью попадал
   * между плюсом и семёркой, и набранный номер вставал ПЕРЕД семёркой: «8 800 550-05-00»
   * превращалось в «+880055005007», кнопка звонка загоралась, а дверь звонка отвечала
   * «не похож на номер» (поймано живым прогоном рабочего места 06.10). Пока в поле одна
   * заготовка, курсор всегда стоит за ней; в набранном номере клик работает как обычно.
   */
  const zaZagotovkoy = (el: HTMLInputElement) => {
    if (el.value === '+7' || el.value === '+') {
      el.setSelectionRange(el.value.length, el.value.length);
      prezhniyKursor.current = znachimyh(el.value);
    }
  };

  const ready = nomer.nabor !== null && !busy && canDial;

  /** Принять строку поля целиком: номер — в поле, отказ — словами, поле прежнее. */
  const prinyat = (syroe: string, vstavka = false) => {
    if (editingDisabled) return false;
    const itog = razobratNomer(syroe, vstavka);

    if (!itog.ok) {
      setOtkaz(SLOVA_OTKAZA[itog.otkaz]);
      // Поле остаётся прежним — и курсор там, где стоял до непринятого знака.
      kursor.current = prezhniyKursor.current;
      pererisovat();

      return false;
    }
    setOtkaz(null);
    setNomer(itog.nomer);

    return true;
  };

  /**
   * Вставка. Целый номер (российский или международный) ЗАМЕНЯЕТ поле: вставляют номер,
   * а не его кусок, и заготовка «+7» перед «8 925 …» не должна становиться его частью.
   * Кусок номера встаёт на место курсора, как при наборе.
   */
  const vstavit = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (editingDisabled) return;
    const tekst = e.clipboardData.getData('text');
    const celyy = razobratNomer(tekst, true);

    if (!celyy.ok) {
      setOtkaz(SLOVA_OTKAZA[celyy.otkaz]);

      return;
    }
    // Целый номер, код или начало номера с плюсом — самостоятельное значение, а не кусок.
    if (celyy.nomer.nabor !== null || celyy.nomer.vid === 'kod' || tekst.trim().startsWith('+')) {
      setOtkaz(null);
      setNomer(celyy.nomer);

      return;
    }

    const el = e.currentTarget;
    const s = el.selectionStart ?? el.value.length;
    const po = el.selectionEnd ?? el.value.length;
    kursor.current = znachimyh(el.value.slice(0, s)) + znachimyh(tekst);
    prinyat(el.value.slice(0, s) + tekst + el.value.slice(po));
  };

  /** Клавиша панели — на место курсора, если человек ставил его в поле; иначе в конец. */
  const klavisha = (znak: string) => {
    const el = pole.current;
    const vPole = el !== null && document.activeElement === el;
    const s = vPole ? (el.selectionStart ?? nomer.pole.length) : nomer.pole.length;
    const po = vPole ? (el.selectionEnd ?? nomer.pole.length) : nomer.pole.length;
    if (vPole) kursor.current = znachimyh(nomer.pole.slice(0, s)) + 1;
    prinyat(nomer.pole.slice(0, s) + znak + nomer.pole.slice(po));
  };

  const steret = () => {
    // Стираем последнюю ЦИФРУ (или знак кода), а не скобку маски.
    const bez = nomer.pole.replace(/[\d*#+][^\d*#+]*$/, '');
    prinyat(bez);
  };

  return (
    <div className="mx-auto max-w-sm">
      <input
        ref={pole}
        readOnly={editingDisabled}
        value={nomer.pole}
        onChange={(e) => {
          kursor.current = znachimyh(e.target.value.slice(0, e.target.selectionStart ?? e.target.value.length));
          prinyat(e.target.value);
        }}
        onSelect={(e) => zapomnitKursor(e.currentTarget)}
        onFocus={(e) => zaZagotovkoy(e.currentTarget)}
        onMouseUp={(e) => zaZagotovkoy(e.currentTarget)}
        onPaste={vstavit}
        aria-label="Номер для набора"
        aria-invalid={otkaz !== null}
        aria-describedby="nabor-zametka"
        inputMode="tel"
        autoComplete="off"
        placeholder="+7"
        className="w-full rounded-ops-md px-4 py-3 text-center text-ops-h1 tabular-nums"
        style={{
          background: 'var(--ops-surface)',
          border: `1px solid ${otkaz !== null ? 'var(--ops-danger)' : 'var(--ops-border)'}`,
          color: 'var(--ops-text)',
        }}
      />

      {/* Отказ и правка за человека — словами, не молча (Д-13). Высота строки постоянная: панель не прыгает. */}
      <div
        id="nabor-zametka"
        role={otkaz !== null ? 'alert' : 'status'}
        className="mt-2 min-h-[2.5rem] text-center text-ops-caption"
        style={{ color: otkaz !== null ? 'var(--ops-danger)' : 'var(--ops-text-secondary)' }}
      >
        {otkaz ?? nomer.zametka ?? ''}
      </div>

      <div className="mt-1 grid grid-cols-3 gap-2">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            disabled={editingDisabled}
            onClick={() => klavisha(key)}
            className="rounded-ops-md py-3 text-ops-h2"
            style={{ background: 'var(--ops-surface)', border: '1px solid var(--ops-border)', color: 'var(--ops-text)' }}
          >
            {key}
          </button>
        ))}
      </div>

      <div className="mt-3 text-center text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
        {line ? 'Звоним с линии:' : 'Линия не закреплена — звонить не через что'}
      </div>

      {linii.length > 1 && onVybor ? (
        <div className="mt-2 flex justify-center">
          <select
            className="ops-field"
            style={{ maxWidth: 360 }}
            value={vybrana ?? line?.id ?? linii[0].id}
            onChange={(e) => onVybor(Number(e.target.value))}
            aria-label="Линия для звонка"
            data-testid="nabor-liniya"
          >
            {linii.map((l) => (
              <option key={l.id} value={l.id}>{l.name}{l.number ? ` · ${l.number}` : ''}</option>
            ))}
          </select>
        </div>
      ) : line ? (
        <div className="mt-2 flex justify-center">
          <span
            className="rounded-ops-pill px-3 py-1 text-ops-label font-semibold"
            style={{ background: 'var(--ops-success-soft)', color: 'var(--ops-success)' }}
            title="Линия у рабочего места одна — выбирать не из чего"
          >
            {line.name}
            {line.number ? ` · ${line.number}` : ''}
          </span>
        </div>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={!ready}
          onClick={() => nomer.nabor !== null && onCall(nomer.nabor)}
          className="flex-1 rounded-ops-md py-3 text-ops-h2 font-semibold disabled:opacity-50"
          style={{ background: 'var(--ops-success)', color: '#fff' }}
        >
          Позвонить
        </button>
        <button
          type="button"
          disabled={editingDisabled}
          onClick={steret}
          aria-label="Стереть последний символ"
          className="rounded-ops-md px-4 text-ops-h2"
          style={{ background: 'var(--ops-surface)', border: '1px solid var(--ops-border)', color: 'var(--ops-text)' }}
        >
          ⌫
        </button>
      </div>

      {!canDial ? (
        <p className="mt-3 text-center text-ops-caption" style={{ color: 'var(--ops-text-secondary)' }}>
          Ручной набор не входит в ваши права.
        </p>
      ) : null}
    </div>
  );
}
